import { ChangeEvent, useEffect, useState } from 'react'

import cn from 'clsx'

import useWatchlists from '@App/hooks/useWatchList'
import { CloseIcon } from '@App/static/icons/ArrowIcons'

import { SearchInput } from '../../../pages/spot/components/SpotOrderMarketsTable/components/SearchInput'
import { WatchlistIcon } from '../../../pages/spot/components/SpotOrderMarketsTable/components/SpotOrderMarketsTableSideBar/WatchlistItem'
import {
  useSpotOrderMarketsTableData,
  useSpotOrderMarketsTableFilters,
} from '../../../pages/spot/components/SpotOrderMarketsTable/model'
import { ModalTypes } from '../modalTypes'
import ModalWrapper from '../ModalWrapper'

import s from './styles.module.scss'
import { Column, Table } from '../../../pages/discover/components/Table'
import { IShowMarketsUpdated } from '../../../pages/spot/components/SpotOrderMarketsTable'
import { TickerPrice } from '../../../pages/discover/components/Fallers/components/TickerPrice'
import { useAppDispatch, useAppSelector } from '@App/hooks'
import {
  clearMarkets,
  getMarkets,
  getTickerData,
  requestMarkets,
  subscribeToPublicService,
  unsubscribeToPublicService,
} from '@App/redux/slices/sockets/PublicServiceConnectionSlice'
import { mapTabToBackendFilter } from '@Redux/interfaces'
import { MinusCircle } from '@App/static/icons/SimpleEarnIcons'
import { Popover } from '../../common/PopOver'
import { AddIcon } from '@App/static/icons/SpotOrderIcons'
import { AddToWatchlist } from '../../../pages/spot/components/SpotOrderMarketsTable/components/AddToWatchlist'
import { Events } from '@App/sockets/types'
import { toFixedUntilLastNonZero } from '@App/helpers/toFixedUntilLastNonZero'
import { MarketStatusIcon } from '../../common/MarketStatusIcon'

type FilterType = 'most_traded' | 'top_risers' | 'top_fallers' | 'most_volatile'

const filters: {
  id: FilterType
  title: string
}[] = [
  { id: 'most_traded', title: 'Most traded' },
  { id: 'top_risers', title: 'Top Risers' },
  { id: 'top_fallers', title: 'Top Fallers' },
  { id: 'most_volatile', title: 'Most Volatile' },
]

export const AddMarketsToWhatchlistModal = ({ isOpen, setIsOpen, parent, styles }: ModalTypes) => {
  const dispatch = useAppDispatch()
  const [selectedFilter, setSelectedFilter] = useState<FilterType | null>(null)
  const [isSearchMode, setIsSearchMode] = useState(false)
  const { addMarket, removeMarket, isMarketInWatchlist, watchlists, activeWatchlistId } = useWatchlists({
    showPortfolio: true,
  })
  const markets = useAppSelector((state) => getMarkets(state))
  const { updatedMarkets, openedTradesMarkets, available_types } = useSpotOrderMarketsTableData({
    markets,
  })

  const activeTab = selectedFilter ?? (isSearchMode ? 'all' : null)
  const backendFilter = activeTab ? mapTabToBackendFilter(activeTab) : null

  useEffect(() => {
    if (!backendFilter) return

    dispatch(clearMarkets())
    dispatch(requestMarkets({ filter: backendFilter }))
  }, [backendFilter, dispatch])

  const filterWatchList = watchlists.find((w) => w.key === selectedFilter) ?? null
  const { filteredItems, search_word, inputSearch } = useSpotOrderMarketsTableFilters({
    available_types,
    selectedTab: activeTab,
    updatedMarkets,
    openedTradesMarkets,
    isMarketInWatchlist,
    getSelectedTabWatchlist: () => filterWatchList,
    favouriteMarketIds: [],
  })
  const handleRemoveMarket = (market_id: number) => {
    if (activeWatchlistId) {
      removeMarket(activeWatchlistId, market_id)
    }
  }
  const handleAddMarket = (market_id: number) => {
    if (activeWatchlistId) {
      addMarket(activeWatchlistId, market_id)
    }
  }
  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedFilter) {
      setSelectedFilter(null)
    }
    setIsSearchMode(!!e.target.value)
    inputSearch(e)
  }
  const filtersApplied = !!search_word || !!selectedFilter
  const showData = filtersApplied ? filteredItems : []
  const columns: Column<IShowMarketsUpdated>[] = [
    {
      title: 'Market',
      key: 'title',
      render: (_, row) => (
        <MarketColumn
          market={row}
          onRemoveMarket={handleRemoveMarket}
          onAddMarket={handleAddMarket}
          isAdded={isMarketInWatchlist(activeWatchlistId ?? '', row.id)}
        />
      ),
      width: '40%',
    },
    {
      title: 'Sell',
      key: 'source_currency',
      width: '20%',
      headerAlign: 'end',
      render: (_, r) => (
        <PriceColumn source_currency={r.source_currency} target_currency={r.target_currency} field="ask" />
      ),
    },
    {
      title: 'Buy',
      key: 'target_currency',
      width: '20%',
      headerAlign: 'end',
      render: (_, r) => (
        <PriceColumn source_currency={r.source_currency} target_currency={r.target_currency} field="bid" />
      ),
    },
    {
      title: 'Change',
      key: 'percentChange',
      width: '20%',
      headerAlign: 'end',
      render: (v) => (
        <span className={cn(s.flexEnd, v < 0 ? s.negative : v > 0 ? s.positive : '')}>
          {v > 0 ? '+' : ''}
          {toFixedUntilLastNonZero(v, 2)}%
        </span>
      ),
    },
  ]
  return (
    <ModalWrapper isOpen={isOpen} setIsOpen={setIsOpen} parent={parent} styles={styles}>
      <div className={s.container}>
        <div className={s.header}>
          Add markets
          <CloseIcon className={s.closeIcon} onClick={setIsOpen} />
        </div>
        <div className={s.content}>
          <SearchInput
            containerClassName={s.inputContainer}
            className={s.input}
            placeholder="Search"
            value={search_word}
            onChange={handleSearch}
          />
          <div className={s.filters}>
            {filters.map((filter) => (
              <div
                key={filter.id}
                className={cn(s.filter, filter.id === selectedFilter && s.active)}
                onClick={() => setSelectedFilter(filter.id)}
              >
                <WatchlistIcon
                  item={{ key: filter.id }}
                  className={s.icon}
                  fillClassName={s.fill}
                  pathFillClassName={s.pathFill}
                  pathStrokeClassName={s.pathStroke}
                />
                {filter.title}
              </div>
            ))}
          </div>
          <Table
            columns={columns}
            data={showData}
            rowKey={(row) => `${row.source_currency}-${row.target_currency}-${row.id}`}
            className={s.table}
            bodyClassName={s.tableBody}
            rowClassName={() => s.tableRow}
          />
        </div>
      </div>
    </ModalWrapper>
  )
}
const MarketColumn = ({
  market,
  isAdded,
  onRemoveMarket,
  onAddMarket,
}: {
  market: IShowMarketsUpdated
  onAddMarket?: (id: number) => void
  onRemoveMarket?: (id: number) => void
  isAdded?: boolean
}) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(
      subscribeToPublicService({
        event: Events.GetRate,
        params: {
          component: 'CurrencyRateBlock',
          source_currency: market.source_currency,
          target_currency: market.target_currency,
        },
      }),
    )

    return () => {
      dispatch(
        unsubscribeToPublicService({
          event: Events.GetRate,
          params: {
            component: 'CurrencyRateBlock',
            source_currency: market.source_currency,
            target_currency: market.target_currency,
          },
        }),
      )
    }
  }, [market.source_currency, market.target_currency])

  return (
    <span className={s.marketTitle}>
      <span className={s.addContainer}>
        <MarketStatusIcon market={market} className={s.marketStatus} />
        {isAdded && <MinusCircle onClick={() => onRemoveMarket?.(market.id)} />}
        {!isAdded && <AddIcon width={16} onClick={() => onAddMarket?.(market.id)} />}
      </span>
      {market.title}
    </span>
  )
}
const PriceColumn = ({
  source_currency,
  target_currency,
  field,
}: {
  source_currency: string
  target_currency: string
  field: 'ask' | 'bid'
}) => {
  const tickerData = useAppSelector((state) => getTickerData(state, source_currency, target_currency))
  const price = tickerData[field]
  return (
    <span className={s.flexEnd}>
      <TickerPrice price={price} classNames={s.tickerValue}>
        {price ?? <span>&mdash;</span>}
      </TickerPrice>
    </span>
  )
}
